import React, { useEffect, useState } from 'react';
import { Button, Input, Table, message, Modal, Form, Switch, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    SearchOutlined,
    PlusOutlined,
    GlobalOutlined,
    EditOutlined,
    LoginOutlined,
    ShoppingOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import StatusPill from './components/StatusPill';
import TableToolbar, { Density, ToggleableColumn } from './components/TableToolbar';
import { usePersistedState } from './hooks/usePersistedState';
import axiosInstance from './utils/axiosInstance';
import { resetOrderTimer } from './utils/orderTimer';
import { money, count } from './utils/format';
import Swal from 'sweetalert2';
import { confirmDialog, errorDialog, successDialog } from './utils/dialog';
import { useNavigate } from 'react-router-dom';

/** Drives the 列 picker. Order matches the table; locked entries can't be hidden. */
const COLUMN_LABELS: ToggleableColumn[] = [
    { key: 'id', label: 'ID', locked: true },
    { key: 'username', label: '用户名', locked: true },
    { key: 'phoneNo', label: '手机号' },
    { key: 'orderCount', label: '总订单' },
    { key: 'totalCommission', label: '佣金' },
    { key: 'walletBalances', label: '余额' },
    { key: 'referralId', label: '上级ID' },
    { key: 'creditScore', label: '信誉分' },
    { key: 'referralCode', label: '邀请码' },
    { key: 'commissionRate', label: '佣金 (%)' },
    { key: 'bonus', label: 'Bonus' },
    { key: 'vipLevel', label: 'VIP' },
    { key: 'withdraw', label: '提款？' },
    { key: 'status', label: '状态？' },
    { key: 'createdAt', label: '注册日期' },
    { key: 'password', label: '密码' },
    { key: 'ip', label: 'IP' },
    { key: 'duplicate', label: '防刷' },
];

/** A titled row of action buttons inside a user's expanded panel. */
const ActionGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-faint">{title}</div>
        <div className="flex flex-wrap gap-2">{children}</div>
    </div>
);

interface UserType {
    key: React.Key;
    id: number;
    username: string;
    phoneNo: string;
    referralId?: number;
    walletBalances: number;
    clickRemaining: number;
    totalCommission: number;
    creditScore: number;
    referralCode: string;
    commissionRate: number;
    bonus: number;
    vipLevel: number;
    withdrawEnable: boolean;
    isActive: boolean;
    disableLoginMessage?: string;
    disableMessageType?: string;
    orderCountPrompt?: number;
    orderCountMessage?: string;
    orderCount: number;
    createdAt: Date;
    password: string;
    withdrawalPassword: string;
    userIP: string;
    totalDuplicate: number;
    duplicateUsers: string;
}


const UserPage: React.FC = () => {
    const [users, setUsers] = useState<UserType[]>([]);
    // Every user's row is expanded by default so all actions show below them.
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState<string>();
    const [phoneNo, setPhoneNo] = useState<string>();
    const [ip, setIp] = useState<string>();

    const [visibleCols, setVisibleCols] = usePersistedState<Record<string, boolean>>(
        'adminUserColumns', {}
    );
    const [density, setDensity] = usePersistedState<Density>('adminUserDensity', 'middle');
    const [productSets, setProductSets] = useState<any[]>([]);

    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [editingUser, setEditingUser] = useState<UserType | null>(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);

    const openEditModal = (user: UserType) => {
        setEditingUser(user);
        form.setFieldsValue({
            username: user.username,
            phoneNo: user.phoneNo,
            walletBalances: user.walletBalances,
            clickRemaining: user.clickRemaining,
            totalCommission: user.totalCommission,
            creditScore: user.creditScore,
            commissionRate: user.commissionRate,
            bonus: user.bonus,
            vipLevel: user.vipLevel,
            withdrawEnable: user.withdrawEnable,
            isActive: user.isActive,
            password: '',
            withdrawalPassword: '',
        });
        setIsEditModalVisible(true);
    };

    const handleEditSubmit = async (values: any) => {
        if (!editingUser) return;
        try {
            await axiosInstance.post('admin/protected/editUser', {
                id: editingUser.id,
                ...values
            });
            message.success('用户信息已更新');
            setIsEditModalVisible(false);
            getUsers();
        } catch (error: any) {
            errorDialog(error?.response?.data?.message || '更新失败');
        }
    };

    useEffect(() => {
        getUsers();
        // Fetch product sets for assignment
        axiosInstance.post('admin/protected/getProductSets').then(res => {
            if (res.data?.data) {
                setProductSets(res.data.data);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function formatDate(date: Date) {
        const options: any = { day: '2-digit', month: 'short', year: 'numeric' };
        const datePart = date.toLocaleDateString('en-GB', options).replace(',', '');

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${datePart} ${hours}:${minutes}`;
    }

    const getUsers = async () => {
        setLoading(true);
        try {
        const response = await axiosInstance.post("admin/protected/allUsers", {
            username,
            phoneNo,
            ip: (ip ?? "").trim()
        })
        const data = response?.data?.data;
        if (data) {
            console.log(data)
            const mapped: UserType[] = data.map((e: any) => {
                return {
                    id: e.id,
                    key: e.id,
                    username: e.username,
                    phoneNo: e.phoneNo,
                    clickRemaining: e.clickRemaining,
                    totalCommission: e.totalCommission,
                    walletBalances: Number(e.walletBalances.toFixed(2)),
                    referralId: e.referralId,
                    creditScore: e.creditScore,
                    referralCode: e.referralCode,
                    commissionRate: e.commissionRate,
                    bonus: e.bonus,
                    vipLevel: e.vipLevel,
                    withdrawEnable: e.withdrawEnable,
                    isActive: e.isActive,
                    disableLoginMessage: e.disableLoginMessage,
                    disableMessageType: e.disableMessageType,
                    orderCountMessage: e.orderCountMessage,
                    orderCountPrompt: e.orderCountPrompt,
                    orderCount: e.orderCount,
                    createdAt: new Date(e.createdAt),
                    password: e.password,
                    withdrawalPassword: e.withdrawalPassword,
                    userIP: e.userIP,
                    totalDuplicate: e.totalDuplicate,
                    duplicateUsers: e.duplicateUsers,
                } as UserType;
            });
            setUsers(mapped);
            setExpandedKeys(mapped.map((u) => u.id));
        }
        } finally {
            setLoading(false);
        }
    }

    // add: increment · deduct: decrement · set: overwrite to the exact amount.
    const changeBalance = (user: UserType, mode: 'add' | 'deduct' | 'set') => {
        const id = user.id;
        const title = mode === 'set' ? '设置余额' : mode === 'deduct' ? '扣除余额' : '添加余额';

        Swal.fire({
            title,
            input: "number",
            inputValue: mode === 'set' ? String(user.walletBalances) : undefined,
            inputAttributes: {
                autocapitalize: "off",
                step: "0.01",
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (amount) => {
                try {
                    await axiosInstance.post("admin/protected/adjustBalance", {
                        id: id,
                        amount: amount,
                        mode: mode,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const addTenAndBonus = async (user: UserType) => {
        if (!await confirmDialog({ title: `确认加款10和bonus 1500 给 ${user.username}？` })) return;
        try {
            await axiosInstance.post("admin/protected/adjustBalance", {
                id: user.id,
                amount: 10,
                mode: 'add',
            });
            await axiosInstance.post("admin/protected/adjustBonus", {
                id: user.id,
                bonus: 1500, // adjustBonus API takes the amount to add/deduct (in backend it does user.bonus + bonus)
            });
            getUsers();
            message.success('操作成功');
        } catch (error: any) {
            errorDialog();
        }
    }

    const resetWithdrawErrors = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: `重置 ${user.username} 提款错误次数？` })) return;
        try {
            await axiosInstance.post("admin/protected/resetWithdrawFailedCount", {
                id: id,
            })
            getUsers();
        } catch (error: any) {
            errorDialog();
        }
    }

    const resetUserClick = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: '确定重置点击？', text: `重置 ${user.username} 的点击次数` })) return;
        try {
            await axiosInstance.post("admin/protected/clickReset", {
                userId: id,
            })
            getUsers();
        } catch (error: any) {
            errorDialog();
        }
    }

    const resetUserPassword = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "新密码",
            input: "text",
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (newPassword) => {
                try {
                    const res = await axiosInstance.post("admin/protected/changeUserPassword", {
                        id: id,
                        newPassword: newPassword,
                    })
                    if (res.status == 201) {
                        successDialog();
                        getUsers();
                    } else {
                        errorDialog();
                    }

                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustCreditScore = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "更改信誉分",
            input: "number",
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (score) => {
                try {
                    await axiosInstance.post("admin/protected/adjustCreditScore", {
                        id: id,
                        score: score,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustCommissionRate = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "更改Commission (%)",
            input: "number",
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (commissionRate) => {
                try {
                    await axiosInstance.post("admin/protected/adjustCommissionRate", {
                        id: id,
                        commissionRate: commissionRate,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustBonus = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "添加 Bonus",
            input: "number",
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (bonus) => {
                try {
                    await axiosInstance.post("admin/protected/adjustBonus", {
                        id: id,
                        bonus: bonus,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustVipLevel = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: 'VIP 等级',
            input: "number",
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (vipLevel) => {
                try {
                    await axiosInstance.post("admin/protected/adjustVipLevel", {
                        id: id,
                        vipLevel: vipLevel,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustWithdrawEnable = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: `${user.withdrawEnable ? '关闭' : '开启'}提款？` })) return;
        try {
            await axiosInstance.post("admin/protected/adjustWithdrawEnable", {
                id: id,
                withdrawEnable: !user.withdrawEnable,
            })
            getUsers();
        } catch (error: any) {
            errorDialog();
        }
    }

    const adjustAccountStatusLevel = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: `${user.isActive ? "停用" : "开启"} 账号？`,
            html: `
            <div class='flex flex-col'>
                <select id='select-input' class='border border-solid'>
                    <option value="info" ${user.disableMessageType == "info" ? "selected" : ""}>提示</option>
                    <option value="warning" ${user.disableMessageType == "warning" ? "selected" : ""}>警告</option>
                    <option value="error" ${user.disableMessageType == "error" ? "selected" : ""}>错误</option>
                    <option value="success" ${user.disableMessageType == "success" ? "selected" : ""}>成功</option>
                </select>` +
                `<textarea id="swal-input2" class="swal2-input !min-h-[200px]" rows='4' placeholder='停用自定义信息'>` +
                (user.disableLoginMessage ?? "") +
                `</textarea>
                </div>`,
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                let input = (document.getElementById("select-input") as any)?.value ?? "info"
                let message = (document.getElementById("swal-input2") as any)?.value

                try {
                    await axiosInstance.post("admin/protected/adjustAccountStatus", {
                        id: id,
                        isActive: !user.isActive,
                        disableMessageType: input,
                        disableLoginMessage: message,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustOrderCountPrompt = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "卡单号码",
            input: "number",
            inputValue: user.orderCountPrompt,
            inputAttributes: {
                autocapitalize: "off",
                placeholder: "自定义卡单内容",
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (orderCountPrompt) => {
                try {
                    await axiosInstance.post("admin/protected/adjustOrderCountPrompt", {
                        id: id,
                        orderCountPrompt: orderCountPrompt,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const adjustOrderCountMessage = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "卡单内容",
            input: "text",
            inputValue: user.orderCountMessage,
            inputAttributes: {
                autocapitalize: "off",
                placeholder: "自定义卡单内容",
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (orderCountMessage) => {
                try {
                    await axiosInstance.post("admin/protected/adjustOrderCountMessage", {
                        id: id,
                        orderCountMessage: orderCountMessage,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const resetUserWithdrawalPassword = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "新提款密码",
            input: "text",
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (newPassword) => {
                try {
                    const res = await axiosInstance.post("admin/protected/changeUserWithdrawalPassword", {
                        id: id,
                        newPassword: newPassword,
                    })
                    if (res.status == 201) {
                        successDialog();
                        getUsers();
                    } else {
                        errorDialog();
                    }

                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }


    const adjustCommission = (user: UserType) => {
        const id = user.id;

        Swal.fire({
            title: "加扣 Commission",
            input: "number",
            inputAttributes: {
                autocapitalize: "off",
                step: "0.01",
            },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (amount) => {
                try {
                    await axiosInstance.post("admin/protected/adjustCommissionAmount", {
                        id: id,
                        amount: amount,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const deleteUser = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: `确认删除用户 ${user.username}？` })) return;
        try {
            await axiosInstance.post("admin/protected/deleteUser", {
                id: id,
            })
            getUsers();
        } catch (error: any) {
            errorDialog();
        }
    }

    const setTimer = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: `确定为 ${user.username} 设置定时？` })) return;
        try {
            await axiosInstance.post("admin/protected/setTimer", {
                id: id,
            })
            getUsers();
        } catch (error: any) {
            errorDialog();
        }
    }

    const removeTimer = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: `确定移除 ${user.username} 的定时？` })) return;
        try {
            await axiosInstance.post("admin/protected/removeTimer", {
                id: id,
            })
            getUsers();
        } catch (error: any) {
            errorDialog();
        }
    }

    const editPhone = (user: UserType) => {
        Swal.fire({
            title: '修改手机号',
            input: 'text',
            inputValue: user.phoneNo,
            inputAttributes: { autocapitalize: 'off' },
            showCancelButton: true,
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            showLoaderOnConfirm: true,
            preConfirm: async (phoneNo) => {
                try {
                    await axiosInstance.post('admin/protected/updateUserPhone', {
                        id: user.id,
                        phoneNo,
                    })
                    getUsers();
                } catch (error: any) {
                    errorDialog();
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    }

    const impersonate = async (user: UserType) => {
        try {
            const res = await axiosInstance.post('admin/protected/loginAsUser', { id: user.id });
            const token = res?.data?.token;
            if (token) {
                // Hand the storefront the freshly-minted user token; it stores it and drops in as them.
                window.open(
                    `${process.env.REACT_APP_WEBSITE_URL}/login?token=${encodeURIComponent(token)}`,
                    '_blank'
                );
            } else {
                errorDialog('无法登入该用户');
            }
        } catch (error: any) {
            errorDialog();
        }
    }

    const assignSet = async (user: UserType) => {
        const options: Record<string, string> = {};
        productSets.forEach(s => {
            options[s.id] = `${s.name} (${s.commission}%)`;
        });

        if (Object.keys(options).length === 0) {
            message.error('暂无配套，请先创建');
            return;
        }

        const { value: setId } = await Swal.fire({
            title: '选择配套',
            input: 'select',
            inputOptions: options,
            inputPlaceholder: '选择配套',
            showCancelButton: true,
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputValidator: (value) => {
                return new Promise((resolve) => {
                    if (value) {
                        resolve(null);
                    } else {
                        resolve('请选择配套');
                    }
                });
            }
        });

        if (setId) {
            const selectedSet = productSets.find(s => s.id === Number(setId));
            if (!selectedSet) return;
            try {
                await axiosInstance.post('admin/protected/addUserSet', {
                    userId: user.id,
                    commission: selectedSet.commission,
                    set: selectedSet.id,
                });
                getUsers();
                message.success('配套已分配');
            } catch (error) {
                message.error('操作失败');
            }
        }
    }

    const assignSetDirectly = async (user: UserType, setId: number, commission: number) => {
        if (!await confirmDialog({ title: `确认分配此配套？` })) return;
        try {
            await axiosInstance.post('admin/protected/addUserSet', {
                userId: user.id,
                commission: commission,
                set: setId,
            });
            getUsers();
            message.success('配套已分配');
        } catch (error) {
            message.error('操作失败');
        }
    }

    const resetUserOrderTimer = async (user: UserType) => {
        const id = user.id;

        if (!await confirmDialog({ title: `重新启动 ${user.username} 的订单计时器？` })) return;
        try {
            await resetOrderTimer({
                userId: id,
            })
            getUsers();
            message.success('已重新启动计时器');
        } catch (error: any) {
            errorDialog(error?.response?.data?.message || error?.message || '操作失败');
        }
    }

    /** The full stack of action buttons shown below each user, mirroring the storefront admin. */
    const renderUserActions = (user: UserType) => (
        <div className="sticky left-0 max-w-[min(1080px,calc(100vw-320px))] space-y-4 py-1 pr-4">
            <div className="flex flex-wrap gap-2">
                <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(user)}>编辑</Button>
                <Button size="small" icon={<ShoppingOutlined />} onClick={() => navigate(`/user/${user.id}`)}>商品</Button>
                <Button size="small" type="primary" icon={<LoginOutlined />} onClick={() => impersonate(user)}>登入</Button>
            </div>

            <ActionGroup title="余额 & 密码">
                <Button size="small" onClick={() => addTenAndBonus(user)}>10/1500</Button>
                <Button size="small" onClick={() => changeBalance(user, 'add')}>加款</Button>
                <Button size="small" onClick={() => changeBalance(user, 'deduct')}>扣款</Button>
                <Button size="small" onClick={() => changeBalance(user, 'set')}>设置余额</Button>
                <Button size="small" onClick={() => adjustBonus(user)}>加扣 Bonus</Button>
                <Button size="small" onClick={() => adjustCommission(user)}>加扣 Commission</Button>
                <Button size="small" onClick={() => resetUserPassword(user)}>重置密码</Button>
                <Button size="small" onClick={() => resetUserWithdrawalPassword(user)}>重置提款密码</Button>
            </ActionGroup>

            <ActionGroup title="账号控制">
                <Button size="small" onClick={() => adjustCreditScore(user)}>信誉分</Button>
                <Button size="small" onClick={() => adjustCommissionRate(user)}>佣金比例</Button>
                <Button size="small" onClick={() => adjustVipLevel(user)}>设置VIP</Button>
                <Button size="small" onClick={() => adjustWithdrawEnable(user)}>
                    {user.withdrawEnable ? '关闭' : '开启'} Withdraw
                </Button>
                <Button size="small" onClick={() => resetWithdrawErrors(user)}>重置提款错误次数</Button>
                <Button size="small" onClick={() => adjustAccountStatusLevel(user)}>
                    {user.isActive ? '停用' : '开启'} 账号
                </Button>
                <Button size="small" onClick={() => adjustOrderCountPrompt(user)}>设置卡单</Button>
                <Button size="small" onClick={() => adjustOrderCountMessage(user)}>卡单内容</Button>
            </ActionGroup>

            <ActionGroup title="连单预设">
                {productSets.map(s => (
                    <Button key={s.id} size="small" onClick={() => assignSetDirectly(user, s.id, s.commission)}>
                        {s.name}
                    </Button>
                ))}
                <Button size="small" onClick={() => resetUserClick(user)}>重置点击计数器</Button>
                <Button size="small" onClick={() => resetUserOrderTimer(user)}>重新启动确认计时器</Button>
            </ActionGroup>

            <ActionGroup title="定时 & 删除">
                <Button size="small" onClick={() => setTimer(user)}>设置定时</Button>
                <Button size="small" onClick={() => removeTimer(user)}>取消定时</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteUser(user)}>删除用户</Button>
            </ActionGroup>
        </div>
    );

    const text = (s?: string) => (s ?? '').toString();

    const allColumns: ColumnsType<UserType> = [
        { key: 'id', title: 'ID', dataIndex: 'id', fixed: 'left', width: 70, sorter: (a, b) => a.id - b.id },
        {
            key: 'username', title: '用户名', fixed: 'left', width: 130,
            sorter: (a, b) => text(a.username).localeCompare(text(b.username)),
            render: (_, r) => <span className="font-semibold text-ink">{r.username}</span>,
        },
        {
            key: 'phoneNo', title: '手机号', dataIndex: 'phoneNo', className: 'tabular', width: 145,
            sorter: (a, b) => text(a.phoneNo).localeCompare(text(b.phoneNo)),
        },
        {
            key: 'orderCount', title: '总订单', className: 'tabular', width: 110,
            sorter: (a, b) => a.orderCount - b.orderCount,
            render: (_, r) => count(r.orderCount),
        },
        {
            key: 'totalCommission', title: '佣金', className: 'tabular', width: 110,
            sorter: (a, b) => a.totalCommission - b.totalCommission,
            render: (_, r) => money(r.totalCommission),
        },
        {
            key: 'walletBalances', title: '余额', className: 'tabular', width: 120,
            sorter: (a, b) => a.walletBalances - b.walletBalances,
            render: (_, r) => <span className="font-semibold text-ink">{money(r.walletBalances)}</span>,
        },
        {
            key: 'referralId', title: '上级ID', dataIndex: 'referralId', className: 'tabular', width: 110,
            sorter: (a, b) => (a.referralId ?? 0) - (b.referralId ?? 0),
        },
        {
            key: 'creditScore', title: '信誉分', dataIndex: 'creditScore', className: 'tabular', width: 110,
            sorter: (a, b) => a.creditScore - b.creditScore,
        },
        {
            key: 'referralCode', title: '邀请码', width: 135,
            sorter: (a, b) => text(a.referralCode).localeCompare(text(b.referralCode)),
            render: (_, r) => <span className="tabular tracking-wider text-body">{r.referralCode}</span>,
        },
        {
            key: 'commissionRate', title: '佣金 (%)', dataIndex: 'commissionRate', className: 'tabular', width: 125,
            sorter: (a, b) => a.commissionRate - b.commissionRate,
        },
        {
            key: 'bonus', title: 'Bonus', className: 'tabular', width: 110,
            sorter: (a, b) => a.bonus - b.bonus,
            render: (_, r) => money(r.bonus),
        },
        {
            key: 'vipLevel', title: 'VIP', width: 100,
            sorter: (a, b) => a.vipLevel - b.vipLevel,
            render: (_, r) => (
                <span className="inline-flex items-center rounded-md bg-violet-50 px-2 py-0.5
                                 text-xs font-medium text-violet-700 ring-1 ring-inset ring-violet-200">
                    VIP {r.vipLevel}
                </span>
            ),
        },
        {
            key: 'withdraw', title: '提款？', width: 120,
            sorter: (a, b) => Number(a.withdrawEnable) - Number(b.withdrawEnable),
            render: (_, r) => <StatusPill on={r.withdrawEnable} onLabel="开启" offLabel="关闭" />,
        },
        {
            key: 'status', title: '状态？', width: 120,
            sorter: (a, b) => Number(a.isActive) - Number(b.isActive),
            render: (_, r) => <StatusPill on={r.isActive} onLabel="开启" offLabel="停用" />,
        },
        {
            key: 'createdAt', title: '注册日期', width: 185,
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
            render: (_, r) => <span className="whitespace-nowrap text-muted">{formatDate(r.createdAt)}</span>,
        },
        {
            key: 'password', title: '密码', width: 170,
            render: (_, r) => (
                <div className="text-xs leading-relaxed">
                    <div><span className="text-faint">登入: </span>{r.password}</div>
                    <div><span className="text-faint">提款: </span>{r.withdrawalPassword}</div>
                </div>
            ),
        },
        {
            key: 'ip', title: 'IP', width: 130,
            sorter: (a, b) => text(a.userIP).localeCompare(text(b.userIP)),
            render: (_, r) => <span className="tabular text-muted">{r.userIP}</span>,
        },
        {
            key: 'duplicate', title: '重叠用户/银行防刷', width: 140,
            sorter: (a, b) => a.totalDuplicate - b.totalDuplicate,
            render: (_, r) => (
                r.totalDuplicate <= 0
                    ? <span className="text-faint">无</span>
                    : (
                        <div className="text-xs leading-relaxed">
                            <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5
                                             font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                                重叠 {r.totalDuplicate}
                            </span>
                            <div className="text-muted">用户ID: {r.duplicateUsers}</div>
                        </div>
                    )
            ),
        },
    ];

    const shownColumns = allColumns.filter((c) => visibleCols[String(c.key)] !== false);

    // Fixed layout needs a real total; recompute it so hiding columns shrinks the scroll area.
    const scrollX = shownColumns.reduce((sum, c) => sum + (Number(c.width) || 120), 0);

    return (
        <AppShell
            title="会员"
            subtitle={`${users.length} 用户`}
            actions={
                <>
                    <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate("/newuser")}>新建</Button>
                    <Button icon={<GlobalOutlined />} onClick={() => window.open(process.env.REACT_APP_WEBSITE_URL)}>打开网站</Button>
                </>
            }
        >
            <Panel className="mb-4">
                <div className='flex flex-wrap gap-3'>
                    <Input className='max-w-[220px]' allowClear placeholder="用户名" onChange={(e) => setUsername(e.target.value)} onPressEnter={getUsers} />
                    <Input className='max-w-[220px]' allowClear placeholder="手机号" onChange={(e) => setPhoneNo(e.target.value)} onPressEnter={getUsers} />
                    <Input className='max-w-[220px]' allowClear placeholder="IP" onChange={(e) => setIp(e.target.value)} onPressEnter={getUsers} />
                    <Button type="primary" icon={<SearchOutlined />} onClick={getUsers}>搜索</Button>
                </div>
            </Panel>

            <Panel flush>
            {loading ? <TableSkeleton rows={6} columns={7} /> : (
            <div className="p-4 pb-0">
                <TableToolbar
                    columns={COLUMN_LABELS}
                    visible={visibleCols}
                    onToggle={(key, next) => setVisibleCols((prev) => ({ ...prev, [key]: next }))}
                    onReset={() => setVisibleCols({})}
                    density={density}
                    onDensityChange={setDensity}
                >
                    共 {users.length} 位会员
                </TableToolbar>
            </div>
            )}
            {loading ? null : (
            <Table<UserType>
                dataSource={users}
                columns={shownColumns}
                scroll={{ x: scrollX }}
                size={density}
                sticky
                expandable={{
                    expandedRowKeys: expandedKeys,
                    onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                    expandedRowRender: (record) => renderUserActions(record),
                }}
                rowClassName={(record) =>
                    !record.isActive ? 'row-inactive'
                        : record.totalDuplicate > 0 ? 'row-flagged'
                            : ''
                }
                locale={{
                    emptyText: (
                        <div className="py-12 text-center">
                            <div className="text-[15px] font-medium text-body">没有会员</div>
                            <div className="mt-1 text-[13px] text-faint">调整搜索条件，或点击「新建」添加会员</div>
                        </div>
                    ),
                }}
                pagination={{
                    showSizeChanger: true,
                    defaultPageSize: 20,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`,
                }}
            />
            )}
            </Panel>
            <Modal
                title="编辑用户信息"
                open={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleEditSubmit}
                >
                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item label="用户名" name="username" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="手机号" name="phoneNo" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        
                        <Form.Item label="密码 (留空则不修改)" name="password">
                            <Input.Password placeholder="输入新密码" />
                        </Form.Item>
                        <Form.Item label="提款密码 (留空则不修改)" name="withdrawalPassword">
                            <Input.Password placeholder="输入新提款密码" />
                        </Form.Item>

                        <Form.Item label="余额" name="walletBalances" rules={[{ required: true }]}>
                            <InputNumber className="w-full" step="0.01" />
                        </Form.Item>
                        <Form.Item label="剩余点击 (Click Remaining)" name="clickRemaining" rules={[{ required: true }]}>
                            <InputNumber className="w-full" />
                        </Form.Item>

                        <Form.Item label="总佣金" name="totalCommission" rules={[{ required: true }]}>
                            <InputNumber className="w-full" step="0.01" />
                        </Form.Item>
                        <Form.Item label="信誉分" name="creditScore" rules={[{ required: true }]}>
                            <InputNumber className="w-full" />
                        </Form.Item>

                        <Form.Item label="佣金比例 (%)" name="commissionRate" rules={[{ required: true }]}>
                            <InputNumber className="w-full" step="0.1" />
                        </Form.Item>
                        <Form.Item label="Bonus" name="bonus" rules={[{ required: true }]}>
                            <InputNumber className="w-full" step="0.01" />
                        </Form.Item>

                        <Form.Item label="VIP等级" name="vipLevel" rules={[{ required: true }]}>
                            <InputNumber className="w-full" />
                        </Form.Item>
                        
                        <div className="flex gap-8">
                            <Form.Item label="账号状态" name="isActive" valuePropName="checked">
                                <Switch checkedChildren="启用" unCheckedChildren="停用" />
                            </Form.Item>
                            <Form.Item label="提现功能" name="withdrawEnable" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                        </div>
                    </div>
                    
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setIsEditModalVisible(false)} className="mr-2">取消</Button>
                        <Button type="primary" htmlType="submit">保存</Button>
                    </div>
                </Form>
            </Modal>
        </AppShell>
    );
}

export default UserPage;
